import { ChangeDetectionStrategy, Component, OnInit, ViewChild } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { TdLoadingService } from '@covalent/core/loading';
import { findIndex, nth } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, publish, refCount, switchMap } from 'rxjs/operators';
import { DutyLogHttpClient } from 'src/app/shared/flight-log-api/duty-logs/duty-log-client.service';
import { EmployeeHttpClient } from 'src/app/shared/flight-log-api/employees/employee-client.service';
import { DutyLogEntity } from 'src/store/duty-log/duty-log.model';
import { filterNull } from 'src/util/rxjs/operators';
import { EmployeeViewModel } from 'src/view-models/user-view-model';
import { DutyEditFormComponent } from '../duty-edit-form/duty-edit-form.component';

@Component({
  selector: 'fl-duty-edit-page',
  templateUrl: './duty-edit-page.component.html',
  styleUrls: ['./duty-edit-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DutyEditPageComponent implements OnInit {
  public loadingKey = 'duty-edit-form';
  public employee$: Observable<EmployeeViewModel>;
  public pilotName: string;
  public dutyLogId: string;
  public componentDestruction$ = new Subject();
  public dutyLogData$: Observable<{
    current: DutyLogEntity;
    previous: DutyLogEntity;
    next: DutyLogEntity;
  }>;
  @ViewChild(DutyEditFormComponent)
  public formCmp: DutyEditFormComponent;

  constructor(
    private _dutyLogs: DutyLogHttpClient,
    private _employees: EmployeeHttpClient,
    private _snackBar: MatSnackBar,
    private _route: ActivatedRoute,
    private _router: Router,
    private _loading: TdLoadingService,
  ) {}

  public ngOnInit() {
    const dutyLogEntry$ = this._route.params.pipe(
      // tslint:disable-next-line: no-non-null-assertion
      map((param) => param.id!),
      filter((p) => p != null),
      switchMap((entryId) => this._dutyLogs.getById(entryId)),
      publish(),
      refCount(),
    );
    this.employee$ = dutyLogEntry$.pipe(
      filterNull(),
      switchMap((dutyLog) => this._employees.getById(dutyLog.employeeId)),
      filterNull(),
      map((e) => new EmployeeViewModel(e)),
      publish(),
      refCount(),
    );
    const dutyLogEntries$ = combineLatest([dutyLogEntry$, this.employee$]).pipe(
      switchMap(([dutyLogEntry, employee]) => {
        return this._dutyLogs.getMany({ pilotEmployeeId: employee.id }).pipe(
          map((result) => result.results),
          map((logs) => {
            // tslint:disable-next-line: no-non-null-assertion
            const index = findIndex(logs, (log) => log.id === dutyLogEntry!.id);
            const prev = nth(logs, index + 1);
            const next = index === 0 ? null : nth(logs, index - 1);
            return index > -1 ? [logs[index], prev, next] : [];
          }),
        );
      }),
      publish(),
      refCount(),
    );
    // tslint:disable-next-line: no-non-null-assertion
    const currentLogEntry$ = dutyLogEntries$.pipe(map((logs) => logs[0]!));
    // tslint:disable-next-line: no-non-null-assertion
    const prevLogEntry$ = dutyLogEntries$.pipe(map((logs) => logs[1]!));
    // tslint:disable-next-line: no-non-null-assertion
    const nextLogEntry$ = dutyLogEntries$.pipe(map((logs) => logs[2]!));

    // ensure that all data is loaded before loading the form.
    this.dutyLogData$ = combineLatest([currentLogEntry$, prevLogEntry$, nextLogEntry$]).pipe(
      map(([cur, prev, next]) => ({
        current: cur,
        previous: prev,
        next,
      })),
    );
  }

  public async update() {
    this._loading.register(this.loadingKey);
    try {
      const dutyLogEntry = this.formCmp.form.value;
      await this._dutyLogs.update(dutyLogEntry).toPromise();
      this._snackBar.open('Successfully updated duty log entry.', undefined, {
        duration: 2000,
      });

      this._router.navigate(['duty-logs']);
    } catch (err) {
      this._snackBar.open('Error updating duty log entry.', undefined, {
        duration: 2000,
      });
      throw err;
    } finally {
      this._loading.resolveAll(this.loadingKey);
    }
  }
}
